import { validatePromoCodeMutation } from '@/api/carts/cartMutations';
import { cartQuery } from '@/api/carts/cartQueries';
import { createCheckoutMutation } from '@/api/checkout/checkoutMutations';
import { countriesQuery } from '@/api/countries/countryQueries';
import { parcelTerminalsListQuery } from '@/api/parcels/parcelQueries';
import { queryKeys } from '@/api/queryKeys';
import { currentUserQuery } from '@/api/user/userQueries';
import { CompleteProfileDialog } from '@/components/auth/CompleteProfileDialog';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { Heading } from '@/components/common/Heading';
import { Input } from '@/components/common/Input';
import { Section } from '@/components/common/Section';
import { Spinner } from '@/components/common/Spinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardsContainer,
} from '@/components/dashboard/DashboardCard';
import { CreateAddressFormDialog } from '@/components/dashboard/account/CreateAddressFormDialog';
import { CartItemsCard } from '@/components/dashboard/my-carts/CartItemsCard';
import { CartSummary } from '@/components/dashboard/my-carts/CartSummarry';
import { SecuredPayment } from '@/components/dashboard/my-carts/SecuredPayment';
import type { Country } from '@/domain/Country';
import { useFormMutation } from '@/hooks/useFormMutation';
import { checkoutSchema } from '@/validation/checkoutValidator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notFound, useParams } from '@tanstack/react-router';
import { useFormik } from 'formik';
import { useState } from 'react';
import type { z } from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

interface CheckoutCardTitleProps {
  title: string;
  subtitle: string;
}

const CheckoutCardTitle = ({ title, subtitle }: CheckoutCardTitleProps) => {
  return (
    <div className="flex w-full items-center justify-between text-xl">
      <p>{title}</p>

      <p>{subtitle}</p>
    </div>
  );
};

function getShippingPrice(
  country: Country | undefined,
  total: number,
  deliveryMethod: string,
): number | null {
  if (!country) return null;
  if (total >= country.free_shipping) return 0;
  if (deliveryMethod === 'door_to_door') {
    return Number(country.shipping) || 0;
  }
  return Number(country.terminal_shipping) || 0;
}

type CheckoutFormValues = z.infer<typeof checkoutSchema>;

export const MyCartCheckoutPage = () => {
  const { cartId } = useParams({
    from: '/_dashboard-layout/dashboard/my-carts/$cartId/checkout',
  });

  const [isItemsListOpen, setIsItemsListOpen] = useState(false);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const queryClient = useQueryClient();

  const { data: cart, isLoading, isError } = useQuery(cartQuery(cartId));
  const { data: user } = useQuery(currentUserQuery);
  const { data: countriesData } = useQuery(countriesQuery);

  const addresses = user?.data.addresses;
  const address = addresses?.[addresses.length - 1];
  const { data: parcelTerminals } = useQuery({
    ...parcelTerminalsListQuery(),
  });

  const formik = useFormik<CheckoutFormValues>({
    initialValues: {
      deliveryMethod: 'door_to_door',
      parcelTerminalId: '',
      address_id: address?.id || 0,
      promo_code: '',
    },
    validate: toFormikValidate(checkoutSchema),
    onSubmit: async (values) => {
      const terminal =
        values.deliveryMethod === 'post_machine' && values.parcelTerminalId
          ? parcelTerminals?.data.find((p) => p.id === values.parcelTerminalId)
          : undefined;

      await createCheckout.mutateAsync({
        cart_id: Number(cartId),
        address_id: values.address_id,
        package: 'Single',
        terminal,
        promo_code: validatePromoCode.isSuccess ? values.promo_code : undefined,
      });
    },
    enableReinitialize: true,
  });

  const createCheckout = useFormMutation({
    ...createCheckoutMutation,
    formik,
  });

  const validatePromoCode = useMutation(validatePromoCodeMutation(cartId));

  const handleProceedClick = () => {
    if (!address) {
      setShowCompleteProfile(true);
    } else {
      formik.handleSubmit();
    }
  };

  const handleApplyPromoCode = () => {
    const promoCode = (formik.values.promo_code || '').trim();
    if (!promoCode) {
      return;
    }

    validatePromoCode.mutate({
      promo_code: promoCode,
    });
  };

  const addressString = address
    ? `${address.line1} ${address.city} ${address.country.country} ${address.zipcode}`
    : 'No address set';

  const cartTotal =
    validatePromoCode.data?.data.price_breakdown?.total ??
    cart?.data.price_breakdown?.total ??
    0;

  const parcelTerminal =
    formik.values.deliveryMethod === 'post_machine'
      ? parcelTerminals?.data.find(
          (p) => p.id === formik.values.parcelTerminalId,
        )
      : undefined;

  const country = countriesData?.data.find(
    (c) =>
      c.country_code ===
      (parcelTerminal?.countryCode || address?.country.country_code),
  );
  const shippingPrice = getShippingPrice(
    country,
    cartTotal,
    formik.values.deliveryMethod,
  );

  if (isLoading) {
    return (
      <Section>
        <Card>
          <CardContent className="flex min-h-40 items-center justify-center">
            <Spinner className="size-10" />
          </CardContent>
        </Card>
      </Section>
    );
  }

  if (isError || !cart) {
    throw notFound();
  }

  return (
    <Section>
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
        <CardsContainer className="-mx-4 lg:mx-0">
          <Card>
            <CardHeader>
              <Heading as="h1" size="h2" className="text-secondary text-left">
                Order Summary
              </Heading>
            </CardHeader>

            <CardContent className="flex flex-col gap-5">
              <CheckoutCardTitle
                title="Services and products"
                subtitle={`${((cart.data.price_breakdown?.total ?? 0) / 100).toFixed(2)} €`}
              />

              <ul className="ml-5 list-disc">
                <li>
                  <p>Purchasing the supplements on your behalf.</p>

                  <button
                    className="text-primary hover:text-secondary active:text-secondary cursor-pointer font-medium underline transition-all"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsItemsListOpen(!isItemsListOpen);
                    }}
                  >
                    {isItemsListOpen ? 'Hide' : 'Show'} list of supplements
                  </button>

                  {isItemsListOpen && <CartItemsCard cartId={cartId} />}
                </li>

                <li>Pre-sorting the supplements into individual portions.</li>

                <li>Processing of automatic supplements refills.</li>

                <li>Pharmacist support any time.</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CheckoutCardTitle
                title="Choose delivery method"
                subtitle={
                  shippingPrice === null
                    ? '—'
                    : shippingPrice === 0
                      ? 'Free'
                      : `${(shippingPrice / 100).toFixed(2)} €`
                }
              />
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
              {/* Post machine delivery option */}
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  id="post-machine"
                  name="deliveryMethod"
                  checked={formik.values.deliveryMethod === 'post_machine'}
                  onChange={() => {
                    formik.setFieldValue('deliveryMethod', 'post_machine');
                    formik.setFieldTouched('deliveryMethod', true);
                  }}
                  className="mt-1 size-5 shrink-0 cursor-pointer"
                />
                <div className="flex flex-1 flex-col gap-2">
                  <label
                    htmlFor="post-machine"
                    className="cursor-pointer font-medium"
                  >
                    Post machine delivery
                  </label>

                  {formik.values.deliveryMethod === 'post_machine' && (
                    <Combobox
                      id="parcel-terminal-search"
                      label="Search for machine"
                      items={
                        parcelTerminals?.data.map((parcel) => ({
                          id: parcel.id,
                          value: `${parcel.city} ${parcel.address} ${parcel.postalCode} ${parcel.name} ${parcel.id}`,
                          label: `${parcel.city} ${parcel.address} ${parcel.postalCode} ${parcel.name} ${parcel.id}`,
                        })) || []
                      }
                      value={formik.values.parcelTerminalId}
                      onChange={(value) => {
                        formik.setFieldValue('parcelTerminalId', value || '');
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('parcelTerminalId', true);
                      }}
                      error={
                        formik.touched.parcelTerminalId &&
                        formik.errors.parcelTerminalId
                          ? formik.errors.parcelTerminalId
                          : undefined
                      }
                      touched={formik.touched.parcelTerminalId}
                    />
                  )}
                </div>
              </div>

              {/* Door to door delivery option */}
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  id="door-to-door"
                  name="deliveryMethod"
                  checked={formik.values.deliveryMethod === 'door_to_door'}
                  onChange={() => {
                    formik.setFieldValue('deliveryMethod', 'door_to_door');
                    formik.setFieldTouched('deliveryMethod', true);
                    formik.setFieldValue('parcelTerminalId', '');
                  }}
                  className="mt-1 size-5 shrink-0 cursor-pointer"
                />
                <div className="flex flex-1 flex-col gap-2">
                  <label
                    htmlFor="door-to-door"
                    className="cursor-pointer font-medium"
                  >
                    Door to door delivery
                  </label>

                  {formik.values.deliveryMethod === 'door_to_door' &&
                    address && (
                      <div className="bg-muted flex w-full max-w-108 items-center justify-between gap-2.5 rounded-[0.625rem] px-4 py-3">
                        <p>{addressString}</p>

                        <CreateAddressFormDialog
                          trigger={
                            <button
                              type="button"
                              className="text-primary hover:text-secondary cursor-pointer font-medium transition-colors"
                            >
                              Change
                            </button>
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-5">
              <CartSummary
                cartId={cartId}
                promoPriceBreakdown={
                  validatePromoCode.data?.data.price_breakdown
                }
              />

              {!!user?.data.can_use_discounts && (
                <div className="flex items-center justify-start gap-2">
                  <Input
                    id="promo-code"
                    label="Promo code"
                    type="text"
                    containerClassName="w-fit"
                    className="max-w-65"
                    {...formik.getFieldProps('promo_code')}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                    error={
                      formik.touched.promo_code && formik.errors.promo_code
                        ? formik.errors.promo_code
                        : undefined
                    }
                    touched={!!formik.touched.promo_code}
                  />

                  <Button
                    type="button"
                    onClick={handleApplyPromoCode}
                    disabled={
                      validatePromoCode.isPending ||
                      !(formik.values.promo_code || '').trim()
                    }
                  >
                    {validatePromoCode.isPending ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
              )}

              <Button
                type="button"
                variant="secondary"
                className="w-full max-w-81 self-center"
                disabled={createCheckout.isPending}
                onClick={handleProceedClick}
              >
                {createCheckout.isPending
                  ? 'Processing...'
                  : 'Proceed to payment'}
              </Button>

              {createCheckout.isError && (
                <p className="text-destructive text-center text-sm">
                  {createCheckout.error.message || 'Failed to create checkout'}
                </p>
              )}

              <SecuredPayment className="self-center p-5" />
            </CardContent>
          </Card>
        </CardsContainer>
      </form>

      <CompleteProfileDialog
        open={showCompleteProfile}
        accessToken={localStorage.getItem('access_token') ?? ''}
        refreshToken={localStorage.getItem('refresh_token') ?? ''}
        user={user?.data ?? null}
        onComplete={() => {
          setShowCompleteProfile(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.user.current() });
        }}
      />
    </Section>
  );
};
